import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded-md border px-3 py-1 text-xs font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background',
  {
    variants: {
      color: {
        // Neutral/Default - Professional gray
        default:
          'bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700',

        // Primary - Corporate blue
        primary:
          'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800',

        // Success - Professional green
        success:
          'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800',

        // Warning - Amber/Gold
        warning:
          'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800',

        // Destructive - Professional red
        destructive:
          'bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800',

        // Info - Professional indigo
        info: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950 dark:text-indigo-300 dark:border-indigo-800',

        // Secondary - Warm gray
        secondary:
          'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',

        // Purple - Executive/premium
        purple:
          'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800',
      },
      variant: {
        // Default filled style
        default: '',

        // Outline style
        outline: 'bg-transparent border-2',

        // Subtle style with minimal background
        subtle: 'border-transparent',

        // Soft style with muted backgrounds
        soft: 'border-transparent',

        // Solid style with strong contrast
        solid: 'border-transparent text-white dark:text-white',

        // Secondary variant - Modern gradient with subtle depth
        secondary: 'bg-gradient-to-r from-muted/80 to-muted/60 border-muted-foreground/20 text-muted-foreground hover:from-muted to-muted/80 dark:from-muted/60 dark:to-muted/40',

        // Destructive variant - Attention-grabbing with red tones
        destructive: 'bg-gradient-to-r from-destructive/90 to-destructive/80 border-destructive text-destructive-foreground hover:from-destructive hover:to-destructive/90 shadow-sm',
      },
    },
    compoundVariants: [
      // ===== OUTLINE VARIANTS =====
      {
        variant: 'outline',
        color: 'default',
        className:
          'text-slate-600 border-slate-300 hover:bg-slate-50 dark:text-slate-400 dark:border-slate-600 dark:hover:bg-slate-800/50',
      },
      {
        variant: 'outline',
        color: 'primary',
        className:
          'text-blue-600 border-blue-300 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-600 dark:hover:bg-blue-950/50',
      },
      {
        variant: 'outline',
        color: 'success',
        className:
          'text-emerald-600 border-emerald-300 hover:bg-emerald-50 dark:text-emerald-400 dark:border-emerald-600 dark:hover:bg-emerald-950/50',
      },
      {
        variant: 'outline',
        color: 'warning',
        className:
          'text-amber-600 border-amber-300 hover:bg-amber-50 dark:text-amber-400 dark:border-amber-600 dark:hover:bg-amber-950/50',
      },
      {
        variant: 'outline',
        color: 'destructive',
        className:
          'text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-600 dark:hover:bg-red-950/50',
      },
      {
        variant: 'outline',
        color: 'info',
        className:
          'text-indigo-600 border-indigo-300 hover:bg-indigo-50 dark:text-indigo-400 dark:border-indigo-600 dark:hover:bg-indigo-950/50',
      },
      {
        variant: 'outline',
        color: 'secondary',
        className:
          'text-gray-600 border-gray-300 hover:bg-gray-50 dark:text-gray-400 dark:border-gray-600 dark:hover:bg-gray-800/50',
      },
      {
        variant: 'outline',
        color: 'purple',
        className:
          'text-purple-600 border-purple-300 hover:bg-purple-50 dark:text-purple-400 dark:border-purple-600 dark:hover:bg-purple-950/50',
      },

      // ===== SUBTLE VARIANTS =====
      {
        variant: 'subtle',
        color: 'default',
        className:
          'bg-slate-100/60 text-slate-700 hover:bg-slate-100 dark:bg-slate-800/60 dark:text-slate-300 dark:hover:bg-slate-800',
      },
      {
        variant: 'subtle',
        color: 'primary',
        className:
          'bg-blue-100/60 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50',
      },
      {
        variant: 'subtle',
        color: 'success',
        className:
          'bg-emerald-100/60 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50',
      },
      {
        variant: 'subtle',
        color: 'warning',
        className:
          'bg-amber-100/60 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50',
      },
      {
        variant: 'subtle',
        color: 'destructive',
        className:
          'bg-red-100/60 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50',
      },
      {
        variant: 'subtle',
        color: 'info',
        className:
          'bg-indigo-100/60 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 dark:hover:bg-indigo-900/50',
      },
      {
        variant: 'subtle',
        color: 'secondary',
        className:
          'bg-gray-100/60 text-gray-700 hover:bg-gray-100 dark:bg-gray-800/60 dark:text-gray-300 dark:hover:bg-gray-800',
      },
      {
        variant: 'subtle',
        color: 'purple',
        className:
          'bg-purple-100/60 text-purple-700 hover:bg-purple-100 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50',
      },

      // ===== SOFT VARIANTS =====
      {
        variant: 'soft',
        color: 'default',
        className:
          'bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-700 dark:bg-slate-900/50 dark:text-slate-400 dark:hover:bg-slate-900 dark:hover:text-slate-300',
      },
      {
        variant: 'soft',
        color: 'primary',
        className:
          'bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 dark:bg-blue-950/50 dark:text-blue-400 dark:hover:bg-blue-950 dark:hover:text-blue-300',
      },
      {
        variant: 'soft',
        color: 'success',
        className:
          'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400 dark:hover:bg-emerald-950 dark:hover:text-emerald-300',
      },
      {
        variant: 'soft',
        color: 'warning',
        className:
          'bg-amber-50 text-amber-600 hover:bg-amber-100 hover:text-amber-700 dark:bg-amber-950/50 dark:text-amber-400 dark:hover:bg-amber-950 dark:hover:text-amber-300',
      },
      {
        variant: 'soft',
        color: 'destructive',
        className:
          'bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 dark:bg-red-950/50 dark:text-red-400 dark:hover:bg-red-950 dark:hover:text-red-300',
      },
      {
        variant: 'soft',
        color: 'info',
        className:
          'bg-indigo-50 text-indigo-600 hover:bg-indigo-100 hover:text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-400 dark:hover:bg-indigo-950 dark:hover:text-indigo-300',
      },
      {
        variant: 'soft',
        color: 'secondary',
        className:
          'bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-900/50 dark:text-gray-400 dark:hover:bg-gray-900 dark:hover:text-gray-300',
      },
      {
        variant: 'soft',
        color: 'purple',
        className:
          'bg-purple-50 text-purple-600 hover:bg-purple-100 hover:text-purple-700 dark:bg-purple-950/50 dark:text-purple-400 dark:hover:bg-purple-950 dark:hover:text-purple-300',
      },

      // ===== SOLID VARIANTS =====
      {
        variant: 'solid',
        color: 'default',
        className:
          'bg-slate-600 hover:bg-slate-700 focus:ring-slate-500 dark:bg-slate-700 dark:hover:bg-slate-600',
      },
      {
        variant: 'solid',
        color: 'primary',
        className:
          'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 dark:bg-blue-700 dark:hover:bg-blue-600',
      },
      {
        variant: 'solid',
        color: 'success',
        className:
          'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500 dark:bg-emerald-700 dark:hover:bg-emerald-600',
      },
      {
        variant: 'solid',
        color: 'warning',
        className:
          'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500 dark:bg-amber-700 dark:hover:bg-amber-600',
      },
      {
        variant: 'solid',
        color: 'destructive',
        className:
          'bg-red-600 hover:bg-red-700 focus:ring-red-500 dark:bg-red-700 dark:hover:bg-red-600',
      },
      {
        variant: 'solid',
        color: 'info',
        className:
          'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 dark:bg-indigo-700 dark:hover:bg-indigo-600',
      },
      {
        variant: 'solid',
        color: 'secondary',
        className:
          'bg-gray-600 hover:bg-gray-700 focus:ring-gray-500 dark:bg-gray-700 dark:hover:bg-gray-600',
      },
      {
        variant: 'solid',
        color: 'purple',
        className:
          'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500 dark:bg-purple-700 dark:hover:bg-purple-600',
      },

      // ===== SECONDARY VARIANT COMBINATIONS =====
      {
        variant: 'secondary',
        color: 'primary',
        className:
          'bg-gradient-to-r from-blue-100 to-blue-50 border-blue-300/50 text-blue-700 hover:from-blue-200 hover:to-blue-100 dark:from-blue-950/80 dark:to-blue-900/60 dark:text-blue-300 dark:border-blue-700/50',
      },
      {
        variant: 'secondary',
        color: 'success',
        className:
          'bg-gradient-to-r from-emerald-100 to-emerald-50 border-emerald-300/50 text-emerald-700 hover:from-emerald-200 hover:to-emerald-100 dark:from-emerald-950/80 dark:to-emerald-900/60 dark:text-emerald-300 dark:border-emerald-700/50',
      },
      {
        variant: 'secondary',
        color: 'warning',
        className:
          'bg-gradient-to-r from-amber-100 to-amber-50 border-amber-300/50 text-amber-700 hover:from-amber-200 hover:to-amber-100 dark:from-amber-950/80 dark:to-amber-900/60 dark:text-amber-300 dark:border-amber-700/50',
      },
      {
        variant: 'secondary',
        color: 'purple',
        className:
          'bg-gradient-to-r from-purple-100 to-purple-50 border-purple-300/50 text-purple-700 hover:from-purple-200 hover:to-purple-100 dark:from-purple-950/80 dark:to-purple-900/60 dark:text-purple-300 dark:border-purple-700/50',
      },

      // ===== DESTRUCTIVE VARIANT COMBINATIONS =====
      {
        variant: 'destructive',
        color: 'default',
        className:
          'bg-gradient-to-r from-red-500 to-red-600 text-white border-red-700 hover:from-red-600 hover:to-red-700 shadow-red-500/25',
      },
      {
        variant: 'destructive',
        color: 'warning',
        className:
          'bg-gradient-to-r from-orange-500 to-red-500 text-white border-orange-600 hover:from-orange-600 hover:to-red-600 shadow-orange-500/25',
      },
    ],
    defaultVariants: {
      color: 'default',
      variant: 'default',
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  variant?: 'default' | 'outline' | 'subtle' | 'soft' | 'solid' | 'secondary' | 'destructive';
  color?:
    | 'default'
    | 'primary'
    | 'destructive'
    | 'success'
    | 'info'
    | 'warning'
    | 'secondary'
    | 'purple';
}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, color, variant, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(badgeVariants({ color, variant }), className)}
        {...props}
      />
    );
  },
);

Badge.displayName = 'Badge';

export { Badge, badgeVariants };
